import { useState } from 'react';
import { Check, ChevronsUpDown, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { cn } from '@/lib/utils';
import { stableDoses, combinationDoses, NO_TREATMENT_DOSE } from './doseOptions';
import { formatDose, groupCombinationDoses, doseSearchText } from './formatDose';

interface DoseComboboxProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  hasError?: boolean;
  allowClear?: boolean;
  /**
   * Drop the "no treatment" option. Set on the previous-dose picker, which only
   * renders once the patient is marked as already on levothyroxine — "on
   * treatment" plus "previously no treatment" is self-contradictory. Left off
   * for the recommendation pickers, where proposing discontinuation is valid.
   */
  excludeNoTreatment?: boolean;
}

// doseOptions is static, so the ordering, banding and search index are built
// once at module scope rather than on every render.
const combinationGroups = groupCombinationDoses(combinationDoses);

const searchIndex = new Map(
  [...stableDoses, ...combinationDoses].map((dose) => [
    dose.toLowerCase(),
    { haystack: doseSearchText(dose), daily: formatDose(dose).dailyAverage },
  ]),
);

// A bare number is read as "show me doses around this many mcg/day". ±5 mirrors
// the protocol's Tier 1 window, so the search neighbourhood is the same width as
// the clinical "perfect agreement" one.
const NUMERIC_TOLERANCE = 5;

/**
 * One option row: the schedule reads first, the daily average sits under it.
 * `value` stays the raw doseOptions string — it is cmdk's identity and search
 * key, and it is what gets handed back on select.
 */
const DoseItem = ({
  dose,
  selected,
  onSelect,
}: {
  dose: string;
  selected: boolean;
  onSelect: (dose: string) => void;
}) => {
  const { primary, secondary } = formatDose(dose);
  return (
    <CommandItem
      value={dose}
      onSelect={() => onSelect(dose)}
      className="items-start gap-2"
    >
      <Check className={cn('mt-0.5 h-4 w-4 shrink-0', selected ? 'opacity-100' : 'opacity-0')} />
      <span className="flex flex-col">
        <span>{primary}</span>
        {secondary && <span className="text-xs text-muted-foreground">{secondary}</span>}
      </span>
    </CommandItem>
  );
};

/**
 * Typeahead replacement for the 137-entry flat <Select>. Stable single-
 * strength doses are listed first (most clinicians pick from these);
 * mixed-strength combinations follow, ordered by daily average and split into
 * 25 mcg/day bands.
 *
 * Display only: every option is rendered through formatDose, but the string
 * passed to onChange is always the verbatim doseOptions entry, because that
 * string is the validated key the backend and the concordance math parse.
 */
export const DoseCombobox = ({
  value,
  onChange,
  placeholder = 'Επιλέξτε δοσολογία...',
  hasError,
  allowClear,
  excludeNoTreatment,
}: DoseComboboxProps) => {
  const [open, setOpen] = useState(false);
  const visibleStableDoses = excludeNoTreatment
    ? stableDoses.filter((dose) => dose !== NO_TREATMENT_DOSE)
    : stableDoses;

  const handleSelect = (selected: string) => {
    onChange(selected === value ? '' : selected);
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn(
            'w-full justify-between font-normal',
            !value && 'text-muted-foreground',
            hasError && 'border-red-500 focus:border-red-500',
          )}
        >
          <span className="truncate">{value ? formatDose(value).primary : placeholder}</span>
          <div className="flex items-center gap-1 shrink-0">
            {allowClear && value && (
              <X
                className="w-4 h-4 opacity-50 hover:opacity-100"
                onClick={(e) => {
                  e.stopPropagation();
                  onChange('');
                }}
              />
            )}
            <ChevronsUpDown className="w-4 h-4 opacity-50" />
          </div>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <Command
          filter={(itemValue, search) => {
            const query = search.trim().toLowerCase();
            if (!query) return 1;

            const entry = searchIndex.get(itemValue.toLowerCase());
            // Matches the raw notation ("6 x 88"), the unspaced form ("6x88")
            // and the rendered label ("μαζί", "καθημερινά") alike.
            if ((entry?.haystack ?? itemValue.toLowerCase()).includes(query)) return 1;

            const asNumber = Number(query.replace(',', '.'));
            if (entry && Number.isFinite(asNumber) && asNumber > 0) {
              if (Math.abs(entry.daily - asNumber) <= NUMERIC_TOLERANCE) return 1;
            }
            return 0;
          }}
        >
          <CommandInput placeholder="Αναζήτηση (π.χ. 90 mcg/ημ., 7x50, 200+25)..." />
          <CommandList>
            <CommandEmpty>Δεν βρέθηκαν συνδυασμοί.</CommandEmpty>
            <CommandGroup heading="Σταθερή δόση">
              {visibleStableDoses.map((dose) => (
                <DoseItem key={dose} dose={dose} selected={value === dose} onSelect={handleSelect} />
              ))}
            </CommandGroup>
            {combinationGroups.map((group) => (
              <CommandGroup key={group.heading} heading={group.heading}>
                {group.doses.map((dose) => (
                  <DoseItem key={dose} dose={dose} selected={value === dose} onSelect={handleSelect} />
                ))}
              </CommandGroup>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};
